import { useState, useEffect } from 'react';
import { detectStandaloneDisplay } from '../../utils/ui/safeTopFloor';
import { foundation } from '../../ui/tokens';
import { bg, fg, border, brand, status, semantic as sem } from '../../ui/theme/semanticTokens';
import { alphaOf } from '../../utils/ui/colorAlpha';

/** Shared by every pre-auth screen via AuthScreenShell */
/** Display font — name is historical */
export const SORA = "'Bricolage Grotesque', sans-serif";

export const S = { xs: 4, sm: 8, md: 16, lg: 24, xl: 32, xxl: 48 };

export const C = {
  bg: bg.surfaceMuted,
  /** Full-page message ground (access status): slate-100 in Light, the sign-in black in Dark. */
  page: sem.wash.authPage,
  white: bg.surface,
  brand: fg.default,
  heading: fg.default,
  body: fg.secondary,
  muted: fg.placeholder,
  faint: sem.ink.grayFaint,
  /** Contrast fix (2026-09-24 login audit): subtitle / "Forgot password?" /
   * footer text / divider label were sitting on C.muted or C.faint, both too
   * light against the white card. C.faint is only for disabled / de-emphasised hints. */
  readable: fg.muted,
  /** Unified auth-family title color (login "Welcome back", MFA, Forgot).
   * "Option A / Crisper" header decision (2026-09-24): same ink as
   * C.heading — kept as a separate token because callers ask for it by
   * NAME (a screen title, semantically), not because the value differs. */
  title: fg.default,
  border: border.default,
  /** Premium pass (2026-09-24): #e2e8f0 -> #dfe4ea, per the founder-approved
   * mockup (claude.ai/artifact/S9pi2ADqmoD7nVpRw5eL1x). Standard text inputs
   * ONLY — the borderless code-entry family (OTP cells, recovery/email-
   * recovery codes) is a deliberate different material and never reads this. */
  inputBorder: sem.edge.input,
  focusBorder: brand.primary,
  /** Brand-blue TEXT (links, current step): brand.text — lighter in dark, same as focusBorder in Light. */
  linkText: brand.text,
  focusRing: alphaOf(brand.primary, 0.1),
  btnBg: bg.inkFill,
  btnText: fg.onInkFill,
  panelBg: sem.wash.authPanel,
  panelBorder: sem.wash.authPanelEdge,
  checkmark: sem.chart.paid,
  oauthBorder: sem.edge.gray300,
  oauthHover: sem.tint.grey50,
  oauthText: sem.ink.gray700,
  dividerLine: sem.edge.gray200,
  dividerText: sem.ink.gray400,
  warningBg: status.warningFaint,
  // RD-PLAT-005 / KAN-474 — same amber-800 as semantic.warningStrong
  warningText: foundation.color.semantic.warningStrong,
  warningBorder: status.warningFaintBorder,
  label: sem.ink.gray700,
  panelBgGradientEnd: sem.wash.authPanelEdge,
  /** Success beat (green capsule + check) — shared by login/MFA/reset/invite. */
  success: status.successIcon,
  /** The success capsule's fill + its white label (same green as `success` in Light; deeper in Graphite). */
  successFill: status.successFill,
  onSuccessFill: fg.onPrimary,
  successBg: status.successFaint,
};

/**
 * Card radii for the auth family — 20 on phones, 22 on tablet/desktop (login-modern).
 * Reset/Invite drifted to 22 before this existed; always read from here.
 */
export const AUTH_CARD_RADIUS = { mobile: 20, card: 22 };

/**
 * Exit beat: how long the auth card takes to settle out (opacity + a 6px
 * sink) before the splash / next route takes over. Every leave path — login
 * success, MFA success, reset, invite — uses this ONE number.
 */
export const AUTH_LEAVE_MS = 220;

/**
 * The CrewVex wordmark on every sign-in-family card (login, forgot, 2-step,
 * reset, invite AND the required 2-step setup gate) — ONE size per layout so
 * they can never drift (founder 2026-10-10: 36px phone / 40px tablet+desktop;
 * the loading-screen wordmark stays smaller, 28–32px, and grows into it).
 */
export const AUTH_BRAND_FONT = {
  mobile: '2.25rem',
  card: '2.5rem',
  /** Phone held sideways (KAN-1138, compact stack): 26px. */
  landscape: '1.625rem',
};

/** The auth page's brand ground (2026-09-27): the same soft blue as the
 *  tablet/desktop live-map backdrop, so phone and iPad read as one product.
 *  Always painted as its OWN layer that fades with `morphFadeStyle` — never as
 *  the frame's background — so the sign-in → splash warm-up (a
 *  background-COLOR transition on the frame) stays visible underneath. */
/* Phone "Soft wash" (KAN-758, founder pick 2026-10-10): a whisper of brand
   blue at the top fading into white, behind the phone's sign-in CARD. Still,
   no motion, no image. Tablet/desktop keep the card on the live route map. */
/* 2026-10-21, founder pick: the soft blue eases all the way down, no flat white bottom. */
export const AUTH_GROUND_GRADIENT = sem.wash.authGround;

/**
 * ONE shared motion curve + timing set for the pre-auth family (2026-09-26
 * smoothness pass). `ease` is an iOS-sheet curve — soft start, long settle —
 * chosen specifically to end a tween without a lurch or a snap, which the
 * old `cubic-bezier(0.16, 1, 0.3, 1)` (expo-out, most of the motion in the
 * first 40ms) produced on the step-height morph. Every visible-layout tween
 * on login/forgot/MFA (step morph, card + headline entrance, field messages)
 * reads from here so a curve change is never hand-copied N times again.
 */
export const AUTH_MOTION = {
  ease: 'cubic-bezier(0.32, 0.72, 0, 1)',
  /* Step morph — "Calm Glide" (RD-LUI-005 / KAN-751, founder pick E,
     2026-10-09): the card's height and the pieces that travel with it move on
     ONE even curve (gentle start, gentle stop) — never the fast-start sheet
     curve above, which read as lively rather than calm. New words SETTLE on
     `settleEase` (soft landing). */
  morphMs: 520,
  /* ONE step clock (RD-LUI-015 / KAN-1026, founder-approved demo): card height,
     headline + back-link height, shell padding glide and brand gap all run
     this long on `stepEase`. The content fades (StepCrossfade / softDissolve)
     finish inside it, so the new words land with the card, not after it.
     `morphMs` stays for readers outside the step change. */
  stepMs: 360,
  stepEase: 'cubic-bezier(0.4, 0, 0.2, 1)',
  settleEase: 'cubic-bezier(0.22, 1, 0.36, 1)',
  entranceMs: 520,
};

/**
 * TYPE + RHYTHM SYSTEM — "premium sign-in" pass (2026-09-24, founder-approved
 * mockup: claude.ai/artifact/S9pi2ADqmoD7nVpRw5eL1x). ONE set of numbers for
 * EVERY pre-auth screen at EVERY size (login, 2FA, recovery, email recovery,
 * forgot/sent, set-new-password, dead-link, 2FA-setup gate title, accept-
 * invite) so a rhythm change is never hand-copied N times again — screens
 * read these constants instead of picking their own margins.
 */
export const AUTH_RHYTHM = {
  /** brand wordmark -> title (owned by AuthScreenShell's brand wrapper, not
   * the title itself). Header "A · Balanced" (2026-09-25): brand anchors, the
   * title greets — 18px everywhere. */
  brandToTitleCompact: 18,
  /** header block (title + subtitle) -> the first form control */
  headerToContent: 28,
  /** field label -> its control */
  labelToField: 7,
  /** field -> the next field */
  fieldToField: 18,
  /** last field -> the primary button */
  fieldToButton: 24,
  /** "or" divider, above AND below */
  orGap: 22,
  /** footer link block, above */
  footerGap: 24,
  /** "Step X of Y" indicator -> the first control */
  stepToContent: 24,
  /** help paragraph under the OTP boxes */
  hintUnderCode: 12,
  /** VISIBLE text gap above AND below the alts hairline */
  altsDivider: 14,
};

/** Phone held sideways (KAN-1138): the same keys, tighter, so the whole card down to the primary button fits ~375px tall. */
export const AUTH_RHYTHM_LANDSCAPE = {
  ...AUTH_RHYTHM,
  brandToTitleCompact: 10,
  headerToContent: 14,
  labelToField: 6,
  fieldToField: 12,
  fieldToButton: 16,
  orGap: 14,
  footerGap: 12,
  stepToContent: 12,
  hintUnderCode: 8,
  altsDivider: 12,
};

/** Shared control geometry — text inputs, the primary button, OAuth buttons, OTP cells. */
export const AUTH_CONTROL_HEIGHT = '48px';
export const AUTH_CONTROL_RADIUS = '12px';
/** Control height for a phone held sideways (KAN-1138). */
export const AUTH_CONTROL_HEIGHT_LANDSCAPE = '44px';
export const AUTH_OAUTH_HEIGHT = '46px';
/** Resting (non-focused, non-invalid) shadow under every standard text input. */
export const AUTH_INPUT_RESTING_SHADOW = '0 1px 2px rgba(15,23,42,0.03)';

/**
 * Shared pre-auth screen TITLE — "Option A / Crisper" header decision
 * (2026-09-24), sized by the premium pass. ONE style for every screen
 * (login, MFA, forgot/reset, dead-link, 2FA-setup gate) so a header change is
 * never hand-copied N times again (that drift is exactly what AuthScreenShell
 * was extracted to stop). `isMobilePhone` = true only below the 640px auth
 * breakpoint — tablet and desktop share the larger size. `marginTop` stays 0:
 * the brand -> title gap now lives on AuthScreenShell's brand wrapper
 * (AUTH_RHYTHM.brandToTitle*), never on the title, so it applies uniformly
 * whether a caller wraps the title in an <h1> or a plain <div>.
 */
export function authTitleStyle(isMobilePhone) {
  return {
    fontFamily: SORA,
    fontWeight: 700,
    color: C.heading,
    fontSize: '1.375rem', // 22px everywhere — a calm greeting under the 32px brand mark
    lineHeight: 1.15,
    letterSpacing: '-0.02em',
    marginTop: 0,
  };
}

/** Shared pre-auth SUBTITLE / instruction line — sits 8px under the title. */
export function authSubtitleStyle(sans) {
  return {
    fontFamily: sans,
    fontSize: '0.9375rem', // 15px
    color: C.readable,
    marginTop: '6px',
    lineHeight: 1.5,
  };
}

/** Default bottom rhythm for mobile auth shells (browser mode). */
export const AUTH_MOBILE_BOTTOM_RHYTHM_PX = 16;

/**
 * Bottom pad for 100dvh auth shells.
 *
 * Safari *browser*: `100dvh` already ends above the bottom search/toolbar, so
 * adding `env(safe-area-inset-bottom)` (~34px home indicator) double-counts and
 * leaves a dead grey band. Use rhythm only.
 *
 * Installed PWA / standalone: `100dvh` reaches the physical bottom — keep the
 * home-indicator inset so the card clears it. lvh === dvh in standalone mode,
 * so no sliver term is needed there.
 */
export function mobileAuthBottomPadding(isStandalone, rhythmPx = AUTH_MOBILE_BOTTOM_RHYTHM_PX) {
  if (isStandalone) {
    return `max(${rhythmPx}px, env(safe-area-inset-bottom, 0px))`;
  }
  return `${rhythmPx}px`;
}

/**
 * Bottom pad for the AuthScreenShell (login / 2FA / reset / invite) ONLY.
 *
 * Those shells live inside `authPageViewportStyle`, which pins the route frame
 * to `100lvh` (keyboard-shrink guard), and center their card with auto margins
 * or `justifyContent: center`. In Safari BROWSER `100lvh` is taller than the
 * visible `100dvh` by the hidden toolbar sliver, so the card lands low by HALF
 * the sliver (founder device evidence, safari-top-and-centering, 2026-09-27:
 * ~20pt low). Adding the whole sliver to the bottom pad recenters it on the
 * VISIBLE area. The term is 0 wherever lvh === dvh (installed PWA, desktop);
 * in an Android Chrome tab it is the URL-bar height and the same correction
 * applies. Standalone keeps the home-indicator rule unchanged.
 *
 * NOT for `loginShellSafePadding` (EstimateRespond): that customer page
 * sizes its frame with `min-height: 100dvh`, not 100lvh (the Unsubscribe /
 * email-preferences page owns its own scroller and does not use it), so
 * the sliver there would push the card ABOVE center and add dead scroll.
 */
export function authFrameBottomPadding(isStandalone, rhythmPx = AUTH_MOBILE_BOTTOM_RHYTHM_PX) {
  if (isStandalone) return mobileAuthBottomPadding(true, rhythmPx);
  return `calc(${rhythmPx}px + max(0px, 100lvh - 100dvh))`;
}

/**
 * Mobile auth outer shell: full grey viewport, scrollable column. The card's
 * resting top lives in AuthScreenShell (`authAnchoredTopPadding`) and the card
 * carries `mobileAuthCardFlowStyle` (bottom auto margin), so when the card overflows the viewport (SE-class phones,
 * or any phone with a notice banner) the column top-aligns and scrolls from
 * the top instead of cropping the card ends. `justifyContent: center` here
 * would strand the card top unreachable — same class of bug as the
 * landscape-phone fix in `tabletAuthShellLayout`.
 */
export function mobileAuthShellStyle({
  isStandalone,
  topRhythmPx = AUTH_MOBILE_BOTTOM_RHYTHM_PX,
  sideRhythmPx = AUTH_MOBILE_BOTTOM_RHYTHM_PX,
  bottomRhythmPx = AUTH_MOBILE_BOTTOM_RHYTHM_PX,
} = {}) {
  return {
    position: 'relative',
    backgroundColor: C.page,
    height: '100%',
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    overflowY: 'auto',
    paddingTop: `max(${topRhythmPx}px, env(safe-area-inset-top, 0px))`,
    paddingRight: `max(${sideRhythmPx}px, env(safe-area-inset-right, 0px))`,
    paddingBottom: authFrameBottomPadding(isStandalone, bottomRhythmPx),
    paddingLeft: `max(${sideRhythmPx}px, env(safe-area-inset-left, 0px))`,
  };
}

/**
 * The white card inside the mobile auth shell. Top-aligned under the shell's
 * anchored top padding (see `authAnchoredTopPadding` — the card no longer
 * re-centres when a step changes height); the bottom auto margin keeps any
 * spare room below it. Taller than the shell → the column scrolls. Nothing
 * can overlap, nothing is unreachable.
 */
export const mobileAuthCardFlowStyle = {
  marginTop: 0,
  marginBottom: 'auto',
  flexShrink: 0,
};

/**
 * Outer login/MFA/reset/invite padding (roomy). Bottom pad is display-mode
 * aware — see `mobileAuthBottomPadding`.
 */
export function loginShellSafePadding(isStandalone = false) {
  return {
    paddingTop: 'max(24px, env(safe-area-inset-top, 0px))',
    paddingRight: 'max(20px, env(safe-area-inset-right, 0px))',
    paddingBottom: mobileAuthBottomPadding(isStandalone, 24),
    paddingLeft: 'max(20px, env(safe-area-inset-left, 0px))',
  };
}

/** True in installed PWA / iOS standalone — re-checks on resize (orientation). */
export function useIsStandaloneDisplay() {
  const [standalone, setStandalone] = useState(() =>
    typeof window !== 'undefined' ? detectStandaloneDisplay(window) : false
  );
  useEffect(() => {
    const sync = () => setStandalone(detectStandaloneDisplay(window));
    sync();
    window.addEventListener('resize', sync);
    window.addEventListener('orientationchange', sync);
    return () => {
      window.removeEventListener('resize', sync);
      window.removeEventListener('orientationchange', sync);
    };
  }, []);
  return standalone;
}

/**
 * Full-page auth route viewport — pin to large viewport so
 * `interactive-widget=resizes-content` + keyboard does NOT shrink the shell
 * (which was jumping the login card up). Inner mobile shells still
 * `overflowY: auto` so focused fields can scroll into view.
 */
export function authPageViewportStyle() {
  return {
    height: '100lvh',
    maxHeight: '100lvh',
    overflow: 'hidden',
    width: '100%',
  };
}

function deriveLoginLayoutMode(width) {
  if (width < 640) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
}

/**
 * ONE source of truth for the auth layout mode. The old `isMobileProp`
 * override (Login.js's 767.98px matchMedia) is gone on purpose (2026-09-03):
 * it forced 640–767px windows and landscape SE-class phones into the mobile
 * branch while this derivation said tablet — two truths, one breakpoint.
 */
export function useLoginLayoutMode() {
  const [mode, setMode] = useState(() =>
    deriveLoginLayoutMode(typeof window !== 'undefined' ? window.innerWidth : 1200)
  );
  useEffect(() => {
    const onResize = () => setMode(deriveLoginLayoutMode(window.innerWidth));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mode;
}

/* "Short" viewport flag (landscape phone). Lives in its own file (KAN-981); re-exported so imports keep working. */
export { SHORT_VIEWPORT_MAX_PX, useIsShortViewport } from './useIsShortViewport';

/**
 * STILL CARD TOP (RD-LUI-005, founder pick E, 2026-10-09). The pre-auth card
 * no longer re-centres when a step changes its height — that dragged the
 * wordmark and title 30–60px on every Sign in ⇄ Forgot ⇄ 2-step change. The
 * column is top-aligned instead, with its top padding chosen so the TALLEST
 * block (the login card) still sits centred; every
 * shorter step keeps that same top edge and only its bottom edge eases.
 * `svh` (the small, toolbar-shown viewport) so Safari's toolbar collapsing
 * never nudges the card at rest; an engine without `svh` (iOS < 15.4) gets
 * `vh` — an unknown unit would drop the WHOLE inline declaration and leave 0
 * top padding (under the notch in a PWA). A short screen falls back to the
 * plain rhythm and scrolls, as before. Block heights are the measured login
 * card on dev/prod builds (fallback only — useAuthAnchoredTop measures the
 * real card) — re-measured 2026-10-10 after
 * the Google/Apple row left the card (KAN-578).
 */
/** The centred group sits this much ABOVE true centre (optical lift, founder 2026-10-21). */
export const AUTH_OPTICAL_LIFT_PX = 20;

/* Static fallback ONLY (useAuthAnchoredTop measures the real thing). The WHOLE
   group (wordmark -> legal line) is centred, then lifted by AUTH_OPTICAL_LIFT_PX:
   padding-top = (100svh - block) / 2 with block = group + 2 * lift.
   Phone: header 170 (wordmark 44 + 28 + title 37 + 10 + subtitle 23 + 28) +
   card 300 (2 * 22 padding + ~256 fields) + legal 48 = 518.
   Tablet+: header 209 (48 + 44 + 49 + 10 + 26 + 32) + card 300 + legal 48 = 557. */
export const AUTH_ANCHOR_BLOCK_PX = { mobile: 518 + 2 * AUTH_OPTICAL_LIFT_PX, tablet: 557 + 2 * AUTH_OPTICAL_LIFT_PX };

const supportsSvh = () => typeof CSS !== 'undefined'
  && typeof CSS.supports === 'function'
  && CSS.supports('height', '100svh');

export function authAnchoredTopPadding(rhythmPx, blockPx, svh = supportsSvh()) {
  const unit = svh ? '100svh' : '100vh';
  return `max(${rhythmPx}px, env(safe-area-inset-top, 0px), calc((${unit} - ${blockPx}px) / 2))`;
}

/**
 * Vertical behavior for the tablet-branch auth shells: top-aligned at the
 * anchored padding (above); on a short (landscape-phone) viewport, the plain
 * tightened rhythm so the page scrolls cleanly from the top.
 * Bottom pad is display-mode aware (same Safari double-count rule as mobile).
 */
export function tabletAuthShellLayout(isShortViewport, isStandalone = false) {
  const rhythm = isShortViewport ? S.md : S.xl;
  return {
    justifyContent: 'flex-start',
    paddingTop: isShortViewport
      ? `max(${rhythm}px, env(safe-area-inset-top, 0px))`
      : authAnchoredTopPadding(rhythm, AUTH_ANCHOR_BLOCK_PX.tablet),
    paddingBottom: authFrameBottomPadding(isStandalone, rhythm),
  };
}

export function LoginSpinner() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      style={{ animation: 'spin 0.75s linear infinite', flexShrink: 0 }}
      aria-hidden="true"
    >
      <circle cx="9" cy="9" r="7" stroke={alphaOf(fg.onInkFill, 0.35)} strokeWidth="2" />
      <path d="M9 2a7 7 0 0 1 7 7" stroke={fg.onInkFill} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/** Same animation as LoginSpinner; for light backgrounds (e.g. MFA card). */
export function LoginSpinnerMuted() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      style={{ animation: 'spin 0.75s linear infinite', flexShrink: 0 }}
      aria-hidden="true"
    >
      <circle cx="9" cy="9" r="7" stroke={alphaOf(fg.default, 0.1)} strokeWidth="2" />
      <path d="M9 2a7 7 0 0 1 7 7" stroke={fg.muted} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
